import React, { PureComponent, RefObject } from 'react';
import { Flex } from '../../../common/Flex';
import { FlowSectionHeader } from './FlowSectionHeader';
import Utils from '../../../../common/util/Utils';
import { TVSetLoad } from './TVSetLoad';
import {
    ExtendedMonitoredTv,
    MonitoredEvent,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    TvMeteoAlert,
    TvSetForecastProgress,
} from 'app/model/SOL49';
import { MinusSquareFilled, PlusSquareFilled, SettingFilled } from '@ant-design/icons';
import {
    WholeNetworkSettings,
    WholeNetworkSettingsContent,
    WholeNetworkSettingsTitle,
} from './settings/WholeNetworkSettings';
import { ClosablePopover } from '../../../common/ClosablePopover';
import KeyUtils from '../../../../common/util/KeyUtils';
import { FlightId, HotspotDto, TimePeriod } from 'app/model/CORE';
import memoizeOne from 'memoize-one';
import './GroupedFlowSection.less';
import AppConstants, { NumberOfFlowColumns } from '../../../../common/context/AppConstants';
import { IFlowSection } from './IFlowSection';
import { connect, ConnectedProps } from 'react-redux';
import { GroupedFlowSectionState, setFolded } from 'app/redux/features/dashboard/flow-settings-slice';
import { RootState, store } from 'app/redux/store';
import { selectFilteredTvs, selectFilteredTvSets, setFlowSettings } from 'app/redux/features/dashboard/dashboard-slice';
import AppContext from '../../../../common/context/AppContext';

const mapStateToProps = (state: RootState) => {
    return {
        settings: state.dashboardData.settings,
        folded: state.groupedFlowState.folded,
    };
};

const mapDispatchToProps = { setFlowSettings, setFolded };

const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });
type PropsFromRedux = ConnectedProps<typeof connector>;

type GroupedFlowSectionProps = PropsFromRedux & {
    title: string;
    currentTime: Date;
    monitoredTvList?: Array<ExtendedMonitoredTv>;
    tvSetMap?: { [key: string]: string };
    tvSetProgressMap?: { [key: string]: TvSetForecastProgress };
    tvSetlastUpdate?: { [key: string]: Date };
    monitoredMeasures?: Array<MonitoredMeasure>;
    pendingMeasureProposals: Array<MonitoredMcdmMeasure>;
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    supervisedAreas?: Array<string>;
    nbColumns: NumberOfFlowColumns; // Divider of the number of zones

    selectedTvs?: ExtendedMonitoredTv[];
    onSelectionChange?(tvs: ExtendedMonitoredTv[], selected: boolean);
    tvMeteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
    monitoredEvents?: Array<MonitoredEvent>;
    hotspots?: { [key: string]: Array<HotspotDto> };
};

// WebPack compilation starts failing if this is formatted over multiple lines. Unfortunately this disables Prettier for
// the whole file since range-based ignores are only supported in Markdown.
// prettier-ignore
export class GroupedFlowSection extends PureComponent<GroupedFlowSectionProps, GroupedFlowSectionState> implements IFlowSection {
    private tvSetLoadMap: Map<string, RefObject<TVSetLoad>>;
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    getDisplayedMonitoredTvs = (): Array<ExtendedMonitoredTv> => selectFilteredTvs(store.getState());

    /**
     * Return the monitored TVs, organised by TV Set.
     * The result is memoized for obvious optimisation reasons.
     */
    getMonitoredTvsForTvSet = memoizeOne(
        (
            columns: Array<Array<ExtendedMonitoredTv>>,
            tvSets: Array<string>,
        ): Map<string, Array<ExtendedMonitoredTv>> => {
            if (!columns || !tvSets) return;

            const flatColumns = columns.flat();

            const map = new Map();
            tvSets.forEach(tvSet => {
                map.set(
                    tvSet,
                    flatColumns.filter(monitoredTv => tvSet === this.props.tvSetMap[monitoredTv.refLocation.id]),
                );
            });
            return map;
        },
    );

    /**
     * Return the selected monitored TVs, organised by TV Set.
     * The result is memoized for obvious optimisation reasons.
     * Memoizing a map object allows to maintain the same references for child components (TVSetLoad) that are not impacted by a change of selection
     */
    getSelectedTvsByTvSet = memoizeOne(
        (
            monitoredTvsByTvSet: Map<string, Array<ExtendedMonitoredTv>>,
            selectedTvs: Array<ExtendedMonitoredTv>,
        ): Map<string, Array<ExtendedMonitoredTv>> => {
            if (!monitoredTvsByTvSet) return;

            const selectedTvIds = selectedTvs.map(tv => KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(tv));
            const map = new Map();
            monitoredTvsByTvSet.forEach((value, key) => {
                map.set(
                    key,
                    value.filter(tv => Utils.contains(selectedTvIds, KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(tv))),
                );
            });
            return map;
        },
    );

    setCollapsed = (collapsed: boolean) => {
        this.tvSetLoadMap.forEach(tvSetLoad => {
            tvSetLoad.current.setCollapsed(collapsed);
        });
    };

    handleSettingsChange = (settings: WholeNetworkSettings) => {
        this.props.setFlowSettings(settings);
        this.settingsPopoverRef.current.hide();
    };

    /**
     * Return the monitored TVs how they appear on the screen (by columns, with sorting applied).
     * The result is memoized for obvious optimisation reasons.
     */
    getColumns = memoizeOne((monitoredTvList: Array<ExtendedMonitoredTv>, nbColumns: number) => {
        const zones = AppContext.getZones();
        if (!zones) return [];

        // Organize columns from zone definition
        const groupedZones = new Array<Array<string>>();
        for (let i = 0; i < nbColumns; i++) {
            let groupedZone = new Array<string>();
            for (
                let j = i * (AppConstants.nbFlowsColumns / nbColumns);
                j < (i + 1) * (AppConstants.nbFlowsColumns / nbColumns);
                j++
            ) {
                groupedZone = groupedZone.concat(zones[j]);
            }
            groupedZones.push(groupedZone);
        }

        return groupedZones.map(icaoCodeList => {
            return monitoredTvList
                ?.filter(monitoredTv => {
                    const vsName = this.props.tvSetMap[monitoredTv.refLocation.id];
                    if (!vsName) {
                        console.error('TFV not found in TvSetMap: ', monitoredTv);
                        return false;
                    }
                    return Utils.contains(icaoCodeList, vsName.substring(0, 2));
                })
                .sort((tv1, tv2) => {
                    const vsIdx1 = icaoCodeList.indexOf(this.props.tvSetMap[tv1.refLocation.id].substring(0, 2));
                    const vsIdx2 = icaoCodeList.indexOf(this.props.tvSetMap[tv2.refLocation.id].substring(0, 2));

                    if (vsIdx1 !== vsIdx2) return vsIdx1 - vsIdx2;

                    if (tv1.refLocation.id < tv2.refLocation.id) return -1;
                    if (tv1.refLocation.id > tv2.refLocation.id) return 1;

                    return 0;
                });
        });
    });

    /**
     * Return existing tv meteo alerts for the given list of tvs (which are the tvs of the tvset)
     */
    getTvSetMeteoAlerts = memoizeOne(
        (tvs: Array<ExtendedMonitoredTv>, meteoAlerts: { [key: string]: Array<TvMeteoAlert> }): Array<TvMeteoAlert> => {
            if (!tvs || !meteoAlerts) return;

            //extract only the tv names
            const tvIds = tvs.map(extMonTv => extMonTv.refLocation.id);

            const existingTvsWithMeteoAlerts = Object.keys(meteoAlerts).filter(tv => tvIds.indexOf(tv) > -1);

            const output: Array<TvMeteoAlert> = [];
            for (const tv of existingTvsWithMeteoAlerts) {
                output.push(...meteoAlerts[tv]);
            }
            return output;
        },
    );

    renderContent = (tvs, tvSets) => {
        if (this.props.folded) return undefined;

        const columns: Array<Array<ExtendedMonitoredTv>> = this.getColumns(tvs, this.props.nbColumns);
        const monitoredTvsByTvSet = this.getMonitoredTvsForTvSet(columns, tvSets); // tvs are sorted by display order (sorting logic in this.getColumns)
        const selectedTvsByTvSet = this.getSelectedTvsByTvSet(monitoredTvsByTvSet, this.props.selectedTvs);

        // Extract TV sets per column
        const columnTvSets = columns.map(monitoredTvs => {
            return monitoredTvs
                ?.map(monitoredTv => this.props.tvSetMap[monitoredTv.refLocation.id])
                .filter((value, index, self) => tvSets.includes(value) && self.indexOf(value) === index);
        });

        return (
            <Flex>
                {columns.map((monitoredTvs, colIdx) => {
                    return (
                        <Flex
                            key={'column-' + colIdx + '/' + columns.length}
                            direction="vertical"
                            className="column"
                            size={100 / this.props.nbColumns + '%'}>
                            {columnTvSets[colIdx]?.map(tvSet => {
                                const ref = React.createRef<TVSetLoad>();
                                this.tvSetLoadMap.set(tvSet, ref);

                                // TODO: Extract tvsOccupancyPeriods that are concerned by the TVset (and memoize)
                                return (
                                    <TVSetLoad
                                        ref={ref}
                                        key={'tvset-' + tvSet}
                                        tvSet={tvSet}
                                        progress={this.props.tvSetProgressMap?.[tvSet]}
                                        lastUpdate={this.props.tvSetlastUpdate?.[tvSet]}
                                        monitoredTvs={monitoredTvsByTvSet.get(tvSet)}
                                        currentTime={this.props.currentTime}
                                        monitoredMeasures={this.props.monitoredMeasures}
                                        pendingMeasureProposals={this.props.pendingMeasureProposals}
                                        tvsOccupancyPeriods={this.props.tvsOccupancyPeriods}
                                        selectedTvs={selectedTvsByTvSet.get(tvSet)}
                                        onSelectionChange={this.props.onSelectionChange}
                                        displayOccupancy={
                                            !this.props.settings.showOccupancy ? 'none' : this.props.settings.occupancy
                                        }
                                        displayOccupancyForTv={
                                            !this.props.settings.showOccupancyForTv
                                                ? 'all'
                                                : this.props.settings.occupancyForTv
                                        }
                                        showLoadInactivePeriods={this.props.settings.showLoadInactivePeriods}
                                        supervised={this.isSupervised(tvSet)}
                                        meteoAlerts={this.getTvSetMeteoAlerts(
                                            monitoredTvsByTvSet.get(tvSet),
                                            this.props.tvMeteoAlerts,
                                        )}
                                        monitoredEvents={this.props.monitoredEvents}
                                        hotspots={this.props.hotspots}
                                    />
                                );
                            })}
                        </Flex>
                    );
                })}
            </Flex>
        );
    };

    private isSupervised(tvSet: string) {
        return (
            Utils.isNotEmpty(this.props.supervisedAreas) &&
            Utils.contains(this.props.supervisedAreas, tvSet?.substring(0, 2))
        );
    }

    render() {
        this.tvSetLoadMap = new Map<string, RefObject<TVSetLoad>>();

        const tvSets = selectFilteredTvSets(store.getState());
        const tvs = selectFilteredTvs(store.getState());
        return (
            <Flex direction='vertical' className='grouped-flow-section'>
                <div style={{position: "relative"}}>
                    <FlowSectionHeader
                        title={this.props.title}
                        folded={this.props.folded}
                        onFoldedChange={this.props.setFolded}
                        nbTvs={tvs?.length}
                        nbTvSets={tvSets?.length}
                        extraActions={[
                            <PlusSquareFilled key="expand-all" onClick={() => this.setCollapsed(false)} />,
                            <MinusSquareFilled key="collapse-all" onClick={() => this.setCollapsed(true)} />,
                            <ClosablePopover
                                key="settings-action"
                                ref={this.settingsPopoverRef}
                                trigger="click"
                                placement="bottom"
                                title={<WholeNetworkSettingsTitle />}
                                content={
                                    <WholeNetworkSettingsContent
                                        settings={this.props.settings}
                                        onChange={this.handleSettingsChange}
                                        onClose={() => this.settingsPopoverRef.current.hide()}
                                    />
                                }>
                                <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                            </ClosablePopover>,
                        ]}
                    />
                </div>
                {this.renderContent(tvs, tvSets)}
            </Flex>
        );
    }
}

const GroupedFlowSectionRedux = connector(GroupedFlowSection);
export default GroupedFlowSectionRedux;
